// Forms for pac-eventio connectors, built from their JSON Schemas (GET connectors/).
//
// A connector describes its connection settings and its input/output options as JSON Schema with a
// few UI hints: x-secret (credential), x-options {provider, depends_on} (dropdown filled by the
// connection's option provider once the depends_on fields have values), x-widget (textarea,
// column-mapping, filters), x-advanced (hidden behind "Advanced") and x-order (field order).
// These helpers are pure so the rules can be tested without rendering a form.

export const REDACTED = '***';

const isEmpty = (value) => value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

// Connector values are plain JSON. structuredClone would reject them when they come from the store,
// because Vue's reactive proxies cannot be cloned.
export const cloneJson = (value) => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)));

export const humanize = (key) => key.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase());

export const fieldKind = (schema = {}) => {
    if (schema['x-secret']) return 'secret';
    if (schema['x-options']) return 'options';
    if (Array.isArray(schema.enum)) return 'select';
    if (schema.type === 'boolean') return 'boolean';
    if (schema.type === 'integer' || schema.type === 'number') return 'number';
    if (schema.type === 'object') return 'object';
    if (schema.type === 'array') return 'list';
    if (schema['x-widget'] === 'textarea') return 'textarea';
    return 'text';
};

const valueOrDefault = (schema, values, key) => {
    const value = values?.[key];
    return value === undefined ? schema?.properties?.[key]?.default : value;
};

// Does `values` satisfy the `if` part of an allOf rule (const / enum per property)?
export const matchesCondition = (schema, condition, values) =>
    Object.entries(condition?.properties || {}).every(([key, rule]) => {
        const value = valueOrDefault(schema, values, key);
        if ('const' in rule) return value === rule.const;
        if (Array.isArray(rule.enum)) return rule.enum.includes(value);
        return true;
    });

export const requiredKeys = (schema = {}, values = {}) => {
    const keys = new Set(schema.required || []);
    for (const rule of schema.allOf || []) {
        if (rule.if && matchesCondition(schema, rule.if, values)) {
            (rule.then?.required || []).forEach(key => keys.add(key));
        }
    }
    return keys;
};

// Fields an "anyOf: [{required: [a]}, {required: [b]}]" asks for: one of them is needed.
export const alternatives = (schema = {}) =>
    (schema.anyOf || []).map(option => option.required || []).filter(keys => keys.length > 0);

export const fieldsOf = (schema = {}, values = {}, {exclude = []} = {}) => {
    const required = requiredKeys(schema, values);
    return Object.entries(schema.properties || {})
        .filter(([key]) => !exclude.includes(key))
        .map(([key, fieldSchema], index) => ({
            key,
            schema: fieldSchema,
            label: fieldSchema.title || humanize(key),
            description: fieldSchema.description || '',
            kind: fieldKind(fieldSchema),
            required: required.has(key),
            advanced: Boolean(fieldSchema['x-advanced']),
            widget: fieldSchema['x-widget'] || null,
            options: fieldSchema['x-options'] || null,
            order: fieldSchema['x-order'] ?? 1000 + index,
        }))
        .sort((left, right) => left.order - right.order);
};

export const withDefaults = (schema = {}, values = {}) => {
    const result = {...(values || {})};
    for (const [key, fieldSchema] of Object.entries(schema.properties || {})) {
        if (result[key] === undefined && fieldSchema.default !== undefined) {
            result[key] = cloneJson(fieldSchema.default);
        } else if (fieldSchema.type === 'object' && fieldSchema.properties && result[key] !== undefined) {
            result[key] = withDefaults(fieldSchema, result[key]);
        }
    }
    return result;
};

// A dropdown can be loaded once the fields it depends on (siblings in `context`) have values. The
// option provider receives every current value, e.g. `columns.activity` for the activities list.
export const optionRequest = (optionsSpec, context = {}) => {
    const dependsOn = optionsSpec?.depends_on || [];
    return {
        provider: optionsSpec?.provider,
        ready: Boolean(optionsSpec?.provider) && dependsOn.every(key => !isEmpty(context[key])),
        dependencyKey: JSON.stringify(dependsOn.map(key => context[key] ?? null)),
        params: context,
    };
};

// A secret the server already stores: a reference ({type: 'secret', ...}) or the masked value.
export const isStoredSecret = (value) => value === REDACTED || (value !== null && typeof value === 'object');

// Paths ('a.b') of required fields without a value, including one of each anyOf alternative.
export const missingRequired = (schema = {}, values = {}, prefix = '') => {
    const missing = [];
    for (const field of fieldsOf(schema, values)) {
        const value = values?.[field.key];
        const path = prefix + field.key;
        if (field.kind === 'object' && field.schema.properties) {
            if (field.required || !isEmpty(value)) {
                missing.push(...missingRequired(field.schema, value || {}, `${path}.`));
            }
        } else if (field.required && isEmpty(value)) {
            missing.push(path);
        }
    }
    const options = alternatives(schema);
    if (options.length && !options.some(keys => keys.every(key => !isEmpty(values?.[key])))) {
        missing.push(prefix + options.map(keys => keys.join('+')).join('|'));
    }
    return missing;
};

// Required input fields a graph still misses: its options merged over its workspace's, as the
// backend builds the job file (datasource_service.effective_datasource).
export const graphInputMissing = (sourceSchema, workspaceDatasource, graphDatasource) => missingRequired(
    sourceSchema || {},
    {...(workspaceDatasource?.input?.options || {}), ...(graphDatasource?.input?.options || {})});

// Where the editor's activity names come from when the graph's workspace reads from a connector
// other than Celonis: that connection's `activities` lookup, with the graph's effective input
// options. Null for Celonis (the data model endpoints serve those) or without a connection.
export const graphActivityRequest = (connector, workspaceDatasource, graphDatasource) => {
    const connectionId = workspaceDatasource?.input?.connection_id;
    if (!connectionId || !connector || connector.type === 'celonis') return null;
    if (!(connector.option_providers || []).some(provider => provider.name === 'activities')) return null;
    return {
        connectionId,
        provider: 'activities',
        params: {...(workspaceDatasource?.input?.options || {}), ...(graphDatasource?.input?.options || {})},
    };
};

// Lookup results as the editor lists activities ({activity, table}). The table names the event log,
// so activities are not saved with an empty table name (PAC warns about those).
export const activityEntries = (options, params = {}) => {
    const table = params.table || params.file || 'event_log';
    return (options || []).map(option => ({activity: option.value, table}));
};

// The values to send: empty optional fields are left out (the server applies defaults, and an
// empty string would satisfy an anyOf alternative), stored secrets are sent back unchanged.
export const valuesForSubmit = (schema = {}, values = {}) => {
    const required = requiredKeys(schema, values);
    const result = {};
    for (const [key, value] of Object.entries(values || {})) {
        const fieldSchema = schema.properties?.[key];
        if (fieldSchema?.type === 'object' && fieldSchema.properties && value && typeof value === 'object') {
            const nested = valuesForSubmit(fieldSchema, value);
            if (Object.keys(nested).length || required.has(key)) result[key] = nested;
        } else if (!isEmpty(value) || required.has(key)) {
            result[key] = value;
        }
    }
    return result;
};
